import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import '../../assets/styles/account/account.scss';
import usePageMeta from '../../services/page-meta';
import {buildFormBody, parseJsonResponse, validatePassword} from './helpers';

export default function ChangePasswordByPhonePage() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('meta.changePasswordPhone.title', t('restorePassword.header')),
        description: t('meta.changePasswordPhone.description', t('app.description')),
        keywords: t('meta.changePasswordPhone.keywords', t('app.keywords')),
    });

    const location = useLocation();
    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [smsCode, setSmsCode] = useState('');
    const [hasSmsError, setHasSmsError] = useState(false);
    const [isSmsSubmitting, setIsSmsSubmitting] = useState(false);
    const [verifiedCode, setVerifiedCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [newPasswordRepeatError, setNewPasswordRepeatError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [topCaption, setTopCaption] = useState('');

    useEffect(() => {
        let isCancelled = false;

        fetch(`/api/v1/account/change-password/phone${location.search || ''}`, {credentials: 'same-origin'})
            .then(parseJsonResponse)
            .then(({response, data}) => {
                if (isCancelled) {
                    return;
                }

                if (!response.ok) {
                    throw data;
                }

                setPayload(data);
                setTopCaption(t('restorePassword.smsSent'));
            })
            .catch(() => {
                if (!isCancelled) {
                    window.location.href = '/';
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [location.search, t]);

    const resolvePasswordErrorText = (code) => {
        if (code === 'required') {
            return t('restorePassword.errors.requiredPassword');
        }
        if (code === 'min') {
            return t('restorePassword.errors.passwordMinLength');
        }
        if (code === 'max') {
            return t('restorePassword.errors.passwordMaxLength');
        }
        if (code === 'format') {
            return t('restorePassword.errors.passwordWrongFormat');
        }

        return '';
    };

    const handleSmsSubmit = (event) => {
        event.preventDefault();
        if (isSmsSubmitting || !payload?.phone) {
            return;
        }

        setHasSmsError(false);
        setIsSmsSubmitting(true);

        fetch(payload?.actions?.checkSmsCodeUrl || '/api/v1/restore-password/sms', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: buildFormBody({
                phone: payload.phone,
                code: smsCode,
            }),
        })
            .then(parseJsonResponse)
            .then(({response, data}) => {
                if (!response.ok || !data?.code) {
                    throw data;
                }

                setVerifiedCode(String(data.code));
                setTopCaption(t('restorePassword.newPasswordPrompt'));
            })
            .catch(() => {
                setHasSmsError(true);
            })
            .finally(() => {
                setIsSmsSubmitting(false);
            });
    };

    const handlePasswordSubmit = (event) => {
        event.preventDefault();
        if (isPasswordSubmitting || !payload?.phone || !verifiedCode) {
            return;
        }

        setSubmitError('');

        const passwordCode = validatePassword(newPassword);
        const repeatCode = validatePassword(newPasswordRepeat);
        const passwordError = resolvePasswordErrorText(passwordCode);
        let repeatError = resolvePasswordErrorText(repeatCode);

        if (!repeatError && newPassword !== newPasswordRepeat) {
            repeatError = t('restorePassword.errors.passwordMismatch');
        }

        setNewPasswordError(passwordError);
        setNewPasswordRepeatError(repeatError);

        if (passwordError || repeatError) {
            return;
        }

        setIsPasswordSubmitting(true);

        fetch(payload?.actions?.changePasswordUrl || '/api/v1/account/change-password', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: buildFormBody({
                'new-password': newPassword,
                'new-password-repeat': newPasswordRepeat,
                phone: payload.phone,
                code: verifiedCode,
            }),
        })
            .then(parseJsonResponse)
            .then(({response, data}) => {
                if (!response.ok) {
                    throw data;
                }

                window.location.href = data?.data?.url || '/';
            })
            .catch((errorPayload) => {
                setSubmitError(errorPayload?.error?.message || t('restorePassword.errors.changeFailed'));
            })
            .finally(() => {
                setIsPasswordSubmitting(false);
            });
    };

    if (isLoading) {
        return <section className="section flex-center" />;
    }

    return (
        <section className="section flex-center">
            <div className="authentication-form">
                <div className="restore-by-phone">
                    {!verifiedCode && (
                        <form id="smsConfirmation" method="post" onSubmit={handleSmsSubmit}>
                            <h1>{t('restorePassword.header')}</h1>
                            <div className="sent-text">
                                <span className="js-sms-sent-text">{topCaption}</span>
                            </div>
                            <h5>{t('restorePassword.enterCode')}</h5>
                            <div className="form-group">
                                <input type="hidden" name="phone" value={payload?.phone || ''} />
                                <input
                                    id="code"
                                    name="code"
                                    type="text"
                                    placeholder="XXXXXX"
                                    className="form form-code"
                                    value={smsCode}
                                    onChange={(event) => {
                                        setSmsCode(event.target.value);
                                        setHasSmsError(false);
                                    }}
                                />
                                <div className={`error_text js-error${hasSmsError ? '' : ' hidden'}`}>
                                    <span>{t('restorePassword.invalidCode')}</span>
                                </div>
                            </div>
                            <div className="submit-block">
                                <input type="submit" value={t('restorePassword.confirm')} className="btn" disabled={isSmsSubmitting} />
                            </div>
                        </form>
                    )}
                </div>

                <div className="set-new-password">
                    {verifiedCode && (
                        <form action="#" method="post" id="change-password" onSubmit={handlePasswordSubmit}>
                            <h1>{t('restorePassword.newPasswordHeader')}</h1>
                            <input type="hidden" name="phone" value={payload?.phone || ''} />
                            <div className="form-group block-static-height field_block">
                                <label htmlFor="new-password" className={`form-label${newPassword ? ' active' : ''}`}>
                                    {t('restorePassword.newPasswordLabel')}
                                </label>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="new-password"
                                    id="new-password"
                                    className={`form${newPasswordError ? ' error-field' : ''}`}
                                    value={newPassword}
                                    onChange={(event) => {
                                        setNewPassword(event.target.value);
                                        setNewPasswordError('');
                                        setSubmitError('');
                                    }}
                                    required
                                />
                                <div className="hide-show-link js-show-password-restore">
                                    <span onClick={() => setShowPassword((current) => !current)}>
                                        {showPassword ? t('restorePassword.hide') : t('restorePassword.show')}
                                    </span>
                                </div>
                                <span className={`error_text${newPasswordError ? '' : ' hidden'}`}>{newPasswordError}</span>
                            </div>
                            <div className="form-group block-static-height field_block">
                                <label htmlFor="new-password-repeat" className={`form-label${newPasswordRepeat ? ' active' : ''}`}>
                                    {t('restorePassword.newPasswordRepeatLabel')}
                                </label>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="new-password-repeat"
                                    id="new-password-repeat"
                                    className={`form${newPasswordRepeatError ? ' error-field' : ''}`}
                                    value={newPasswordRepeat}
                                    onChange={(event) => {
                                        setNewPasswordRepeat(event.target.value);
                                        setNewPasswordRepeatError('');
                                        setSubmitError('');
                                    }}
                                    required
                                />
                                <div className="hide-show-link js-show-password-restore">
                                    <span onClick={() => setShowPassword((current) => !current)}>
                                        {showPassword ? t('restorePassword.hide') : t('restorePassword.show')}
                                    </span>
                                </div>
                                <span className={`error_text${newPasswordRepeatError ? '' : ' hidden'}`}>{newPasswordRepeatError}</span>
                            </div>
                            <div className={`error-block${submitError ? '' : ' hidden'}`}>
                                <div className="error_text">{submitError}</div>
                            </div>
                            <div className="submit-block">
                                <input type="submit" value={t('restorePassword.save')} className="btn" disabled={isPasswordSubmitting} />
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
